// 🔹 Stylesheets
import '../../../../index.css';
import '../../../../route.css';
import '../../../../reset.css';

// 🔹 React Core
import React, { useState, useEffect, useRef, FC } from 'react';

// 🔹 Routing
import { useNavigate } from 'react-router-dom';

// 🔹 UI Component Libraries
import { motion, AnimatePresence } from 'framer-motion';
import { message } from 'antd';
import confetti from 'canvas-confetti';

// 🔹 Internal Components
import { Button } from 'src/components/Button/Button';
import { useScreenTime } from 'src/hooks/useScreenTime';
import { trackUserBehavior } from 'src/api/mutations/useUserBehaviorTracker';
import { screenTimeTracker, TRAINER_SECTIONS } from 'src/utils/screenTimeTracker';
import { clearGeneratedProgram } from 'src/store/programStore';

// 🔹 Assets
import MiniPTGIF from 'url:../../../../assets/videos/mini-pt-care.mp4';
import { AxiosError } from 'axios';
import { ApiError, useJoinWaitlist } from 'src/api/mutations/useJoinWaitlist.ts';

// ── SVG shapes from Group_2085663043.svg ────────────────────────────────────
// eslint-disable-next-line import/no-named-as-default-member
const shapeA = confetti.shapeFromPath({
  path: 'M5.087,0 L7.966,0 Q13.053,0 13.053,5.087 L13.053,13.565 Q13.053,18.652 7.966,18.652 L5.087,18.652 Q0,18.652 0,13.565 L0,5.087 Q0,0 5.087,0Z',
  matrix: new DOMMatrix([0.5, 0, 0, 0.5, -3.263, -4.663]),
});

// eslint-disable-next-line import/no-named-as-default-member
const shapeB = confetti.shapeFromPath({
  path: 'M5.087,0 L10.174,0 Q15.261,0 15.261,5.087 L15.261,13.565 Q15.261,18.652 10.174,18.652 L5.087,18.652 Q0,18.652 0,13.565 L0,5.087 Q0,0 5.087,0Z',
  matrix: new DOMMatrix([0.5, 0, 0, 0.5, -3.815, -4.663]),
});

// eslint-disable-next-line import/no-named-as-default-member
const shapeC = confetti.shapeFromPath({
  path: 'M4.698,0 L10.563,0 Q15.261,0 15.261,4.698 L15.261,4.697 Q15.261,9.395 10.563,9.395 L4.698,9.395 Q0,9.395 0,4.697 L0,4.698 Q0,0 4.698,0Z',
  matrix: new DOMMatrix([0.5, 0, 0, 0.5, -3.815, -2.349]),
});

export const ProgramCompleted: FC = () => {
  // Use
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  //State
  const [email, setEmail] = useState<string>('');
  const userType = 'trainer';

  const [success, setSuccess] = useState(false);

  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    type?: string;
    general?: string;
  }>({});

  const { mutate: joinWaitlist, isPending } = useJoinWaitlist();

  // State
  const [feedbackContainer, setFeedbackContainer] = useState<boolean>(false);
  const [feedbackValue, setFeedbackValue] = useState<string | ''>('');

  // Handler
  const feedbackHandler = (value: string) => {
    setFeedbackValue(value || '');
  };

  // 🎉 Fire confetti on mount
  useEffect(() => {
    confetti({
      particleCount: 300,
      shapes: [shapeA, shapeB, shapeC],
      colors: ['#FFA100', '#FFC665'],
      scalar: 2,
      spread: 500,
      origin: { y: -0.1 },
      startVelocity: -35,
    });
  }, []);

  // ── Screen time tracking – Section 15 (/trainer/generated/ai/completed) ──
  useScreenTime(TRAINER_SECTIONS.SECTION_15);

  const handleSubmit = () => {
    if (!email || !userType) return;

    setFieldErrors({});

    // Shared helper – sends the analytics snapshot with the confirmed email.
    // Called on both waitlist success and EMAIL_EXISTS (user already registered).
    const sendBehaviorTracker = (confirmedEmail: string) => {
      const storedSessionId = screenTimeTracker.getStoredSessionId();
      trackUserBehavior({
        ...(storedSessionId ? { sessionId: storedSessionId } : {}),
        email: confirmedEmail,
        data: screenTimeTracker.getReport(),
      })
        .then(() => {
          screenTimeTracker.reset();
        })
        .catch(() => {
          // Silently ignore – analytics are non-critical
        });
    };

    joinWaitlist(
      { email, type: userType },
      {
        onSuccess: () => {
          setSuccess(true);
          sendBehaviorTracker(email);
          // Wipe stored program data – the trainer has finished this session.
          clearGeneratedProgram();
        },
        onError: (err) => {
          const axiosErr = err as AxiosError<ApiError>;
          const apiError = axiosErr.response?.data?.error;

          if (!apiError) {
            setFieldErrors({ general: 'Something went wrong. Please try again.' });
            return;
          }

          // EMAIL_EXISTS means we still know who the user is – send analytics.
          if (apiError.code === 'EMAIL_EXISTS') {
            sendBehaviorTracker(email);
          }

          if (apiError.field === 'email') {
            setFieldErrors({ email: apiError.message });
          } else if (apiError.field === 'type') {
            setFieldErrors({ type: apiError.message });
          } else {
            setFieldErrors({ general: apiError.message });
          }
        },
      },
    );
  };

  return (
    <div className={`miniPT-container miniPT-training-container miniPT-animation`}>
      {/* Absolute Flow: The Animated AI Circle */}
      <div
        className="flex-center-column miniPT-animation-container"
        style={{
          position: 'fixed',
          top: 'calc(var(--padding-top) + 250px + 32px)',
          left: '50%',
          width: 250,
          height: 250,
          zIndex: 0,
          pointerEvents: 'none',
          mixBlendMode: 'lighten',
          translate: 'none',
          opacity: 1,
          transform: 'translate(-50%, -100%)',
        }}
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            mixBlendMode: 'lighten',
          }}
        >
          <source src={MiniPTGIF} type="video/mp4" />
        </video>
        <span className="mini-pt-text">MiniPT Core</span>
      </div>

      <div
        className={`background-wrapper flex-center-column px-16`}
        style={{ position: 'relative', zIndex: 1, width: '100%' }}
        ref={containerRef}
      >
        <div className="flex-center-column animation-margin-top">
          <div className="max-width-576 route-fade-in">
            <h1 className="page-sub-title text-center mt-0 mb-12">
              You rocked the program <br /> creation demo!
            </h1>
            <p className="text-light text-center mt-0 mb-32">
              Be among the first trainers to create smarter, AI-powered programs for your clients.
            </p>
          </div>

          {!success && (
            <div
              className="max-width-420 route-fade-in text-center early-access-container"
              style={{ marginBottom: 40 }}
            >
              <input
                placeholder="Enter your email"
                className="early-access"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ marginBottom: 18 }}
              />
              {fieldErrors.email && (
                <div className="text-danger text-start text-small">{fieldErrors.email}</div>
              )}
              <Button
                onClick={handleSubmit}
                variant="primary"
                className={`bg-gradient-color relative-z-10 continue-manually justify-content-center`}
                disabled={!email || isPending}
              >
                {isPending ? 'Please wait...' : 'Get Early Access'}
              </Button>
            </div>
          )}

          {success && (
            <div className="text-center mt-0 mb-32 max-width-768">
              <h3 className="mt-0">{"You're on the list!"}</h3>
              <span>
                We&apos;ve added <strong>{email}</strong> to our waitlist.
              </span>
            </div>
          )}

          <div className="max-width-576 route-fade-in mt-auto">
            <AnimatePresence>
              {!feedbackContainer && (
                <motion.div
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Button
                    onClick={() => setFeedbackContainer(true)}
                    variant="outline"
                    className="relative-z-10 continue-manually"
                  >
                    I Have Feedback to Offer
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <AnimatePresence>
            {feedbackContainer && (
              <motion.div
                className="max-width-576 text-start"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.4 }}
              >
                <div className="dark-bg-card p-3">
                  <label>
                    We&#39;re building this for trainers like you, your feedback really helps us
                    improve.
                  </label>
                  <textarea
                    placeholder="I would like to see..."
                    rows={4}
                    value={feedbackValue}
                    onChange={(e) => feedbackHandler(e.target.value)}
                  />
                  <Button
                    onClick={() => {
                      message.success('Thanks for your feedback! 🚀');
                      setTimeout(() => {
                        setFeedbackContainer(false);
                        navigate('/');
                      }, 1000);
                    }}
                    variant="primary"
                    className="w-100 bg-white justify-content-center"
                    disabled={!feedbackValue}
                  >
                    {isPending ? 'Loading...' : 'Send Feedback'}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <br />
        </div>
      </div>
    </div>
  );
};
